/**
 * oj-bundle.js — <oj-bundle>
 *
 * Behaviour for sections/oj-bundle.liquid (the Pineneedle Bundle, 2026-10-07),
 * cut down from assets/oj-polo-pack.js to a FIXED set of pieces: one paying
 * piece (any colour, any size) and free pieces locked to one colour.
 *
 * TWO TAPS: a size in the gate (sets every sized piece, closes itself), then
 * "Add all three", which posts the real variants to /cart/add.js and goes
 * straight to /checkout. The two automatic Buy X Get Y discounts in admin make
 * the free pieces free; nothing here changes a price.
 *
 * A free piece with nothing to ship in its colour (sold out, or not created
 * yet) is left out of the bag rather than blocking the gilet; the server
 * already left it out of the "worth" figure.
 *
 * NO MONEY IS FORMATTED HERE: per-variant price/worth/save strings arrive
 * server-rendered in the section's JSON.
 *
 * The button only works when the section rendered data-buyable="true" (the
 * offer is live, or an unpublished theme), or with ?bundle-preview in the URL
 * for an internal test on the live theme.
 */

const STORE_KEY = 'oj:bundle:v1';

const SIZE_ORDER = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'xxxxl'];
const SIZE_ALIASES = {
  small: 's',
  medium: 'm',
  large: 'l',
  'x large': 'xl',
  'extra large': 'xl',
  'xx large': 'xxl',
  'xxx large': 'xxxl',
  'xxxx large': 'xxxxl',
};

const sizeKey = (value) => {
  const k = String(value || '').toLowerCase().replace(/[-_.]/g, ' ').replace(/\s+/g, ' ').trim();
  return SIZE_ALIASES[k] || k;
};
const sizeLabel = (value) => {
  const k = sizeKey(value);
  return SIZE_ORDER.includes(k) ? k.toUpperCase() : String(value || '');
};
const sizeRank = (value) => {
  const i = SIZE_ORDER.indexOf(sizeKey(value));
  return i < 0 ? 99 : i;
};

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const fileOf = (url) => String(url || '').split('?')[0].split('/').pop();

class OjBundle extends HTMLElement {
  connectedCallback() {
    this.pieces = this.readJson('script[data-pieces]', []);
    if (!Array.isArray(this.pieces) || !this.pieces.length) return;

    this.gate = this.querySelector('[data-gate]');
    this.addBtn = this.querySelector('[data-add]');
    this.errorEl = this.querySelector('[data-error]');
    this.cards = Array.from(this.querySelectorAll('[data-slot]'));
    this.pay = this.pieces.findIndex((p) => !p.free);

    let preview = false;
    try {
      preview = new URLSearchParams(window.location.search).has('bundle-preview');
    } catch {
      /* no URL API */
    }
    this.buyable = this.dataset.buyable === 'true' || preview;
    if (this.buyable && this.addBtn?.disabled) {
      this.addBtn.disabled = false;
      const label = this.querySelector('[data-add-label]');
      if (label?.dataset.liveLabel) label.textContent = label.dataset.liveLabel;
    }

    this.state = { size: '', colour: {}, override: {} };
    this.needSize = {};
    this.pieces.forEach((p, i) => {
      if (p.locked) this.state.colour[i] = p.colours[0]?.name || p.defaultColour || '';
    });

    this.abort = new AbortController();
    this.restore();
    this.buildGate();
    this.bind();
    this.paint();

    if (!this.state.size && !window.Shopify?.designMode && this.allSizes().length) this.openGate();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.bannerWatch?.disconnect();
    this.unlock();
  }

  readJson(selector, fallback) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || 'null') ?? fallback;
    } catch {
      return fallback;
    }
  }

  /* ---------- persistence ------------------------------------------- */

  restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(`${STORE_KEY}:${this.id}`) || '{}');
      if (saved.size && this.allSizes().includes(saved.size)) this.state.size = saved.size;
      if (saved.colour && this.pay >= 0 && saved.colour[this.pay]) this.state.colour[this.pay] = saved.colour[this.pay];
      if (saved.override) this.state.override = saved.override;
    } catch {
      /* first visit */
    }
  }

  save() {
    try {
      sessionStorage.setItem(`${STORE_KEY}:${this.id}`, JSON.stringify(this.state));
    } catch {
      /* private mode */
    }
  }

  /* ---------- the model --------------------------------------------- */

  /** Sizes any sized piece ships in, in size order. */
  allSizes() {
    const set = new Set();
    for (const p of this.pieces) {
      if (!p.hasSizes) continue;
      for (const v of p.variants || []) if (v.available && v.size) set.add(v.size);
    }
    return [...set].sort((a, b) => sizeRank(a) - sizeRank(b));
  }

  sizeFor(slot) {
    if (!this.pieces[slot].hasSizes) return '';
    return Object.hasOwn(this.state.override, slot) ? this.state.override[slot] : this.state.size;
  }

  inStock(slot, colour = this.state.colour[slot]) {
    return new Set(
      (this.pieces[slot].variants || []).filter((v) => v.available && v.colour === colour).map((v) => v.size)
    );
  }

  /** A free piece with nothing at all to ship in its colour. */
  unavailable(slot) {
    const p = this.pieces[slot];
    return p.free && !(p.variants || []).some((v) => v.available);
  }

  missing(slot) {
    const size = this.sizeFor(slot);
    return Boolean(size) && !this.inStock(slot).has(size);
  }

  colourWith(slot, size) {
    const p = this.pieces[slot];
    const names = (p.colours || []).map((c) => c.name).filter((n) => this.inStock(slot, n).has(size));
    return names.includes(p.defaultColour) ? p.defaultColour : names[0] || null;
  }

  settleColour(slot) {
    const p = this.pieces[slot];
    const names = (p.colours || []).map((c) => c.name);
    if (!names.length || names.includes(this.state.colour[slot])) return;
    const size = this.sizeFor(slot);
    const stocked = names.filter((n) => this.inStock(slot, n).size);
    const def = p.defaultColour;
    this.state.colour[slot] =
      (size && this.colourWith(slot, size)) ||
      (stocked.includes(def) ? def : stocked[0]) ||
      (names.includes(def) ? def : names[0]);
  }

  resolve(slot) {
    const p = this.pieces[slot];
    const colour = this.state.colour[slot];
    if (!p.hasSizes) {
      return (p.variants || []).find((v) => v.available && (!colour || v.colour === colour)) || null;
    }
    const size = this.sizeFor(slot);
    if (!size) return null;
    return (p.variants || []).find((v) => v.available && v.colour === colour && v.size === size) || null;
  }

  /* ---------- events ------------------------------------------------ */

  bind() {
    const { signal } = this.abort;
    this.addEventListener(
      'click',
      (e) => {
        const t = e.target;
        const gateOpt = t.closest('[data-gate-opts] .oj-bundle__opt');
        if (gateOpt) return this.pickGate(gateOpt);
        if (t.closest('[data-skip]')) return this.closeGate();
        if (t.closest('[data-add]')) return this.add();

        const card = t.closest('[data-slot]');
        if (!card) return;
        const slot = Number(card.dataset.slot);
        const sizeOpt = t.closest('[data-sizes] .oj-bundle__opt');
        if (sizeOpt) return this.pickSize(slot, sizeOpt);
        const swatch = t.closest('[data-swatch]');
        if (swatch) return this.pickColour(slot, swatch.dataset.colour || '');
        const nav = t.closest('[data-nav]');
        if (nav) return this.nudge(card, Number(nav.dataset.nav) || 1);
      },
      { signal }
    );
    this.gate?.addEventListener('click', (e) => e.target === this.gate && this.closeGate(), { signal });
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.gate?.dataset.open === 'true') this.closeGate();
      },
      { signal }
    );
  }

  /* ---------- the gate ---------------------------------------------- */

  buildGate() {
    const host = this.querySelector('[data-gate-opts]');
    if (!host) return;
    host.replaceChildren(
      ...this.allSizes().map((value) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oj-bundle__opt';
        btn.dataset.value = value;
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', value);
        btn.textContent = sizeLabel(value);
        return btn;
      })
    );
  }

  openGate() {
    if (!this.gate) return;
    this.gate.dataset.open = 'true';
    document.documentElement.style.overflow = 'hidden';
    this.lockedByGate = true;
    this.watchBanner();
    const first =
      this.gate.querySelector('.oj-bundle__opt[aria-pressed="true"]') || this.gate.querySelector('.oj-bundle__opt');
    window.setTimeout(() => first?.focus({ preventScroll: true }), reduced() ? 0 : 240);
  }

  closeGate() {
    if (!this.gate) return;
    this.gate.dataset.open = 'false';
    this.unlock();
  }

  /** Shopify's cookie banner sits fixed at the foot of a phone viewport above
   *  everything; reserve the height it intrudes on the card (see oj-polo-pack.js). */
  reserveBanner() {
    if (!this.gate) return;
    let reserve = 0;
    for (const el of document.querySelectorAll('[id^="shopify-pc__banner"]:not(link):not(style)')) {
      if (this.bannerWatch && !this.watched.has(el)) {
        this.watched.add(el);
        this.bannerWatch.observe(el, { attributes: true, childList: true, subtree: true });
      }
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
      const r = el.getBoundingClientRect();
      if (r.height > 0 && r.top > window.innerHeight / 2) {
        const card = this.gate.querySelector('.oj-bundle__card');
        const cardH = card ? card.getBoundingClientRect().height : 0;
        const need = Math.ceil(window.innerHeight + cardH + 32 - 2 * r.top);
        reserve = Math.max(reserve, Math.min(Math.max(need, 0), Math.round(window.innerHeight - r.top)));
      }
    }
    this.gate.style.setProperty('--oj-gate-reserve', `${reserve}px`);
  }

  watchBanner() {
    if (this.bannerWatch) return;
    this.watched = new WeakSet();
    this.bannerWatch = new MutationObserver(() => this.reserveBanner());
    this.bannerWatch.observe(document.body, { childList: true });
    window.addEventListener('resize', () => this.reserveBanner(), { signal: this.abort.signal });
    this.reserveBanner();
  }

  unlock() {
    if (this.lockedByGate) document.documentElement.style.overflow = '';
    this.lockedByGate = false;
  }

  pickGate(btn) {
    this.state.size = btn.dataset.value || '';
    this.state.override = {};
    // Only the paying piece can change colour to find the size; a free piece
    // keeps its locked colour and says so under its sizes.
    if (this.pay >= 0 && this.missing(this.pay)) {
      const colour = this.colourWith(this.pay, this.state.size);
      if (colour) this.state.colour[this.pay] = colour;
    }
    this.paint();
    this.save();
    this.closeGate();
  }

  /* ---------- per-card sizes ---------------------------------------- */

  /** Sizes in stock in the piece's current colour. Sold-out sizes (XXXL) are left
      out rather than shown struck through (operator, 2026-10-08). */
  sizesFor(slot) {
    const stock = this.inStock(slot);
    const values = [];
    for (const v of this.pieces[slot].variants || []) {
      if (v.size && stock.has(v.size) && !values.includes(v.size)) values.push(v.size);
    }
    return values.sort((a, b) => sizeRank(a) - sizeRank(b));
  }

  pickSize(slot, btn) {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    this.state.override[slot] = btn.dataset.value || '';
    delete this.needSize[slot];
    this.showError('');
    this.paint();
    this.save();
  }

  paintSizes(card, slot) {
    const host = card.querySelector('[data-sizes]');
    if (!host) return;
    const p = this.pieces[slot];
    const note = card.querySelector('[data-size-note]');

    if (this.unavailable(slot)) {
      host.replaceChildren();
      if (note) {
        note.hidden = false;
        note.textContent = 'Sold out. The rest of the bundle still applies.';
      }
      return;
    }
    if (!p.hasSizes) {
      if (!host.firstChild) {
        const span = document.createElement('span');
        span.className = 'oj-bundle__onesize';
        span.textContent = 'One size';
        host.replaceChildren(span);
      }
      if (note) note.hidden = true;
      return;
    }

    const current = this.sizeFor(slot);
    const values = this.sizesFor(slot);
    const key = values.join(',');
    if (host.dataset.key !== key) {
      host.dataset.key = key;
      host.replaceChildren(
        ...values.map((value) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'oj-bundle__opt';
          btn.dataset.value = value;
          btn.setAttribute('aria-label', value);
          btn.textContent = sizeLabel(value);
          return btn;
        })
      );
    }
    const stock = this.inStock(slot);
    for (const btn of host.querySelectorAll('.oj-bundle__opt')) {
      const out = !stock.has(btn.dataset.value);
      btn.setAttribute('aria-pressed', String(btn.dataset.value === current && !out));
      if (out) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    }
    if (note) {
      const colour = this.state.colour[slot] || '';
      let text = '';
      if (this.missing(slot)) text = `${sizeLabel(current)} is sold out in ${colour}. Choose another size.`;
      else if (!current && (this.needSize[slot] || Object.hasOwn(this.state.override, slot))) text = `Choose a size in ${colour}.`;
      note.hidden = !text;
      note.textContent = text;
    }
  }

  pickColour(slot, name) {
    if (this.pieces[slot].locked) return;
    this.state.colour[slot] = name;
    const size = this.sizeFor(slot);
    if (size && !this.inStock(slot).has(size)) this.state.override[slot] = '';
    this.paint();
    this.save();
  }

  /* ---------- painting ---------------------------------------------- */

  paint() {
    for (const opt of this.querySelectorAll('[data-gate-opts] .oj-bundle__opt')) {
      opt.setAttribute('aria-pressed', String(opt.dataset.value === this.state.size));
    }
    this.cards.forEach((card) => {
      const slot = Number(card.dataset.slot);
      this.settleColour(slot);
      this.paintSwatches(card, slot);
      const colourName = card.querySelector('[data-colour-name]');
      if (colourName) colourName.textContent = this.state.colour[slot] || colourName.textContent;
      this.paintSizes(card, slot);
      this.paintGallery(card, slot);
    });
    this.paintTotals();
  }

  /** The paying piece's chosen variant carries its own price / worth / save strings. */
  paintTotals() {
    if (this.pay < 0) return;
    const p = this.pieces[this.pay];
    const colour = this.state.colour[this.pay];
    const v =
      this.resolve(this.pay) ||
      (p.variants || []).find((x) => x.colour === colour) ||
      (p.variants || [])[0];
    if (!v) return;
    this.setText('[data-ledger-sum]', v.worth);
    this.setText('[data-ledger-free]', `−${v.save}`);
    this.setText('[data-ledger-total]', v.price);
    this.setText('[data-bar-sum]', v.worth);
    this.setText('[data-bar-total]', v.price);
    const card = this.cards.find((c) => Number(c.dataset.slot) === this.pay);
    const price = card?.querySelector('[data-price]');
    if (price && price.textContent.trim() !== v.price) price.textContent = v.price;
  }

  setText(selector, text) {
    const el = this.querySelector(selector);
    if (el && el.textContent !== text) el.textContent = text;
  }

  paintSwatches(card, slot) {
    const host = card.querySelector('[data-swatches]');
    if (!host) return;
    const options = this.pieces[slot].colours || [];
    host.replaceChildren(
      ...options.map((c) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oj-bundle__swatch';
        btn.dataset.swatch = '';
        btn.dataset.colour = c.name;
        btn.title = c.name;
        btn.setAttribute('aria-label', c.name);
        btn.setAttribute('aria-pressed', String(c.name === this.state.colour[slot]));
        if (!this.inStock(slot, c.name).size) btn.dataset.soldOut = 'true';
        if (c.swatch) {
          const img = document.createElement('img');
          img.src = c.swatch;
          img.alt = '';
          img.loading = 'lazy';
          btn.append(img);
        }
        return btn;
      })
    );
  }

  /* ---------- gallery ------------------------------------------------ */

  nudge(card, dir) {
    const track = card.querySelector('[data-track]');
    track?.scrollBy({ left: dir * track.clientWidth, behavior: reduced() ? 'auto' : 'smooth' });
  }

  syncNav(card, track) {
    const prev = card.querySelector('[data-nav="-1"]');
    const next = card.querySelector('[data-nav="1"]');
    if (!prev || !next) return;
    const many = track.children.length > 1;
    const end = track.scrollWidth - track.clientWidth - 2;
    prev.hidden = !many || track.scrollLeft <= 2;
    next.hidden = !many || track.scrollLeft >= end;
  }

  paintGallery(card, slot) {
    const track = card.querySelector('[data-track]');
    if (!track) return;
    const p = this.pieces[slot];
    const chosen = (p.colours || []).find((c) => c.name === this.state.colour[slot]);
    if (!chosen) return;

    const urls = [];
    for (const url of chosen.gallery || []) {
      if (url && !urls.some((u) => fileOf(u) === fileOf(url))) urls.push(url);
    }
    if (!urls.length) return;

    const key = `${chosen.name}|${urls.length}`;
    if (track.dataset.key === key || track.dataset.pending === key) return;

    const existing = track.querySelector('img');
    const alt = `${p.label}, ${chosen.name}`;

    const commit = () => {
      if (this.state.colour[slot] !== chosen.name) return;
      track.replaceChildren(
        ...urls.map((url, n) => {
          const slide = document.createElement('div');
          slide.className = 'oj-bundle__slide';
          let img;
          if (n === 0 && existing && fileOf(existing.currentSrc || existing.src) === fileOf(url)) {
            img = existing;
            img.alt = alt;
          } else {
            img = document.createElement('img');
            img.src = url;
            img.alt = n === 0 ? alt : '';
            img.loading = n === 0 ? 'eager' : 'lazy';
            img.decoding = 'async';
            img.width = 900;
            img.height = 1350;
          }
          slide.append(img);
          return slide;
        })
      );
      track.scrollLeft = 0;
      track.dataset.key = key;
      delete track.dataset.pending;
      if (!track.dataset.watched) {
        track.dataset.watched = 'true';
        track.addEventListener('scroll', () => this.syncNav(card, track), { passive: true, signal: this.abort.signal });
      }
      this.syncNav(card, track);
    };

    if (existing && fileOf(existing.currentSrc || existing.src) === fileOf(urls[0])) return commit();

    track.dataset.pending = key;
    const next = new Image();
    next.onload = commit;
    next.onerror = () => delete track.dataset.pending;
    next.src = urls[0];
  }

  /* ---------- cart --------------------------------------------------- */

  showError(text) {
    if (!this.errorEl) return;
    this.errorEl.textContent = text;
    this.errorEl.hidden = !text;
  }

  async add() {
    if (this.busy || !this.buyable) return;
    this.showError('');
    if (this.allSizes().length && !this.state.size && !Object.keys(this.state.override).length) return this.openGate();

    const items = [];
    for (let slot = 0; slot < this.pieces.length; slot += 1) {
      if (this.unavailable(slot)) continue;
      const variant = this.resolve(slot);
      if (!variant) {
        this.needSize[slot] = true;
        this.paint();
        this.cards[slot]?.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
        this.showError(`Choose a size for the ${this.pieces[slot].label}.`);
        return;
      }
      items.push({ id: variant.id, quantity: 1 });
    }
    if (this.pay < 0 || !items.length) return;

    this.busy = true;
    this.addBtn?.setAttribute('aria-busy', 'true');
    try {
      const addUrl = window.Theme?.routes?.cart_add_url;
      const res = await fetch(addUrl ? `${addUrl}.js` : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        this.showError(data.description || data.message || 'One of these has just sold out. Choose again and retry.');
        this.busy = false;
        this.addBtn?.removeAttribute('aria-busy');
        return;
      }
      // A private-code page sends the code with the customer (data-checkout from the section).
      window.location.assign(this.dataset.checkout || '/checkout');
    } catch {
      this.showError('That didn’t go through. Please try again.');
      this.busy = false;
      this.addBtn?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('oj-bundle')) customElements.define('oj-bundle', OjBundle);
